import React, {useState} from "react";
import {
  Button,
  Tab,
  Tabs,
  IconButton,
  CircularProgress,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import UnfoldLessIcon from "@material-ui/icons/UnfoldLess";
import ZoomOutMapIcon from "@material-ui/icons/ZoomOutMap";
import OutputWithTests from "common_components/flowmaster/output_with_tests";
import ConfigurationInputs from "routes/data_assets/data_assets_create_view/components/configuration_inputs";
import {DataAssetOverview} from "common/flowmaster/types/data_asset";
import {EnhancedLlmRun} from "common/flowmaster/types/llm_run";
import {OrganisationId} from "common/types/organisation";
import {Definition} from "routes/data_assets/data_assets_create_view/components/data_assets_create_view";
import {WorkflowInputValues} from "common/flowmaster/types/workflow";
import ConfigurationRunInputs from "routes/data_assets/data_assets_create_view/components/configuration_run_inputs";
import {
  PreviewPanel,
  PreviewPanelBody,
  PreviewPanelHeader,
} from "routes/data_assets/util/preview_panel";
import {RunWithFeedback} from "routes/data_assets/util/run_with_feedback_button";

interface OutputPreviewProps {
  definition: DataAssetOverview["definition"];
  config: DataAssetOverview["config"];
  llmRun: EnhancedLlmRun;
  organisationId: OrganisationId;
  selectedProjectId: string;
  selectedDocumentId: string;
  fetchLlmRun: (
    organisationId: OrganisationId,
    runId: EnhancedLlmRun["id"],
  ) => void;
  isRefreshing: boolean;
  definitions: Definition[];
  addTest;
  updateTest;
  deleteTest;
  getTestResults;
  rerunTests;
  isRerunningTests: boolean;
  isDeveloperMode?: boolean;
  onClose: () => void;
  ignoreClickawayRefs: Array<React.RefObject<HTMLElement>>;
}

const OutputPreview = ({
  definition,
  config,
  llmRun,
  organisationId,
  selectedProjectId,
  selectedDocumentId,
  addTest,
  updateTest,
  deleteTest,
  getTestResults,
  rerunTests,
  isRerunningTests,
  fetchLlmRun,
  isRefreshing,
  definitions,
  onClose,
  ignoreClickawayRefs,
}: OutputPreviewProps) => {
  const selectedDefinition = definitions.find(d => d.id === definition.id);
  const selectedConfig = selectedDefinition?.workflow_instances.find(
    instance => instance.id === config.id,
  );
  const inputValues = selectedConfig?.config.inputs
    .values as WorkflowInputValues;

  const [selectedTabValue, setSelectedTabValue] = useState<
    "outputs" | "inputs" | "config"
  >("outputs");

  const [showRerunTestFeedback, setShowRerunTestFeedback] = useState(false);

  const handleRerunTests = () => {
    if (llmRun?.id) {
      rerunTests(llmRun.id);
      setShowRerunTestFeedback(true);

      setTimeout(() => {
        setShowRerunTestFeedback(false);
      }, 1500);
    }
  };

  const handleRefresh = () => {
    if (llmRun?.id) {
      fetchLlmRun(organisationId, llmRun.id);
    }
  };

  const handleTabChange = (_, newValue: "outputs" | "inputs" | "config") => {
    setSelectedTabValue(newValue);
  };

  return (
    <PreviewPanel onClose={onClose} ignoreClickawayRefs={ignoreClickawayRefs}>
      {({setIsInteracting, isFullscreen, setIsFullscreen}) => (
        <>
          <PreviewPanelHeader>
            <Tabs
              value={selectedTabValue}
              onChange={handleTabChange}
              indicatorColor="primary"
            >
              <Tab label="Outputs" value="outputs" />
              <Tab label="Inputs" value="inputs" />
              <Tab label="Configuration" value="config" />
            </Tabs>
            <div style={{display: "flex", gap: "0.5em", alignItems: "center"}}>
              {(isRefreshing || isRerunningTests) && (
                <CircularProgress size={20} />
              )}
              <Button
                size="small"
                onClick={handleRefresh}
                disabled={!llmRun?.id}
                variant="outlined"
              >
                Refresh
              </Button>
              <RunWithFeedback
                bottom
                showStartedFeedback={showRerunTestFeedback}
              >
                <Button
                  size="small"
                  onClick={handleRerunTests}
                  disabled={!llmRun?.id}
                  variant="outlined"
                >
                  Rerun Tests
                </Button>
              </RunWithFeedback>
              <div style={{display: "flex", gap: "0.2em"}}>
                <IconButton
                  size="small"
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  aria-label="Close"
                >
                  {isFullscreen ? <UnfoldLessIcon /> : <ZoomOutMapIcon />}
                </IconButton>
                <IconButton size="small" onClick={onClose} aria-label="Close">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            </div>
          </PreviewPanelHeader>
          <PreviewPanelBody>
            {selectedTabValue === "outputs" && (
              <OutputWithTests
                llmRun={llmRun}
                organisationId={organisationId}
                dataKey="output"
                title="Output"
                groupedResults={llmRun.grouped_results}
                onAddTest={addTest}
                onUpdateTest={updateTest}
                onDeleteTest={deleteTest}
                getTestResults={getTestResults}
                onTestEditorOpen={() => setIsInteracting(true)}
                onTestEditorClose={() => setIsInteracting(false)}
              />
            )}
            {selectedTabValue === "inputs" && (
              <ConfigurationRunInputs
                downstreamRuns={llmRun.downstream_runs}
                organisationId={organisationId}
                selectedProjectId={selectedProjectId}
                selectedDocumentId={selectedDocumentId}
                selectedConfig={selectedConfig}
                selectedDefinition={selectedDefinition}
              />
            )}
            {selectedTabValue === "config" && (
              <div style={{padding: "1em"}}>
                <ConfigurationInputs
                  definitions={definitions}
                  definition={selectedDefinition}
                  inputValues={inputValues}
                  initialInputValues={inputValues}
                  onInputChange={() => {}}
                  organisationId={organisationId}
                  disabled={true}
                />
              </div>
            )}
          </PreviewPanelBody>
        </>
      )}
    </PreviewPanel>
  );
};

export default OutputPreview;
